import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { User, Camp, Member, Admin, RegisterData, ApiResponse, PaginatedResponse, CallSlot, Task, StructuredLocation, NotificationListResponse, MyShiftsResponse, Survey, SurveyQuestion, PendingSurveyItem } from '../types';

const SENSITIVE_LOG_KEYS = new Set([
  'password',
  'newPassword',
  'currentPassword',
  'token',
  'authorization',
  'accessToken',
  'idToken'
]);

const sanitizeForLog = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForLog(item));
  }

  if (value && typeof value === 'object') {
    const sanitized: Record<string, any> = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      if (SENSITIVE_LOG_KEYS.has(key)) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeForLog(nestedValue);
      }
    }
    return sanitized;
  }

  return value;
};

type LeadRoleGrantOptions = {
  replaceExistingRole?: boolean;
};

type CampLeadRoleResponse = {
  message: string;
  memberId: string;
  memberName: string;
  isCampLead: boolean;
  isEventsLead?: boolean;
};

type EventsLeadRoleResponse = {
  message: string;
  memberId: string;
  memberName: string;
  isEventsLead: boolean;
  isCampLead?: boolean;
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    const baseURL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001/api';
    
    console.log('🚀 [DEBUG] API Service initialized with baseURL:', baseURL);
    
    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 second timeout
    });

    // Request interceptor to add auth token and normalize paths
    this.api.interceptors.request.use(
      (config) => {
        // Normalize URL path to prevent double /api prefix
        if (config.url) {
          const baseURL = this.api.defaults.baseURL || '';
          // If baseURL ends with /api and url starts with /api, remove the leading /api from url
          // Handle both /api/ and /api (without trailing slash)
          if (baseURL.endsWith('/api') && (config.url.startsWith('/api/') || config.url === '/api')) {
            config.url = config.url.replace(/^\/api\/?/, '');
            // Ensure url starts with / if it doesn't already
            if (!config.url.startsWith('/')) {
              config.url = '/' + config.url;
            }
            console.log('🔧 [API Interceptor] Normalized path to prevent double /api prefix:', config.url);
          }
        }

        // Define public endpoints that don't require authentication.
        // IMPORTANT: Use exact or narrow prefix matching only.
        // Broad prefixes (e.g. "/camps") can accidentally strip auth headers from protected routes
        // like "/camps/my-camp", causing login loops.
        //
        // Three tiers:
        //   • publicExactEndpoints / publicPrefixEndpoints — strictly public. No
        //     token is attached even if one exists in localStorage. Use for
        //     auth-flow endpoints where stale tokens could cause login loops.
        //   • optionalAuthPrefixEndpoints — the backend's `optionalAuth`
        //     middleware accepts both anonymous and authenticated requests,
        //     AND the response differs when the user is authenticated (e.g.
        //     /camps/public/:slug returns 403 for private camps to
        //     anonymous users but returns the full profile to the camp's
        //     owner / Camp Lead / system admin). For these we attach the
        //     token if present but stay quiet when it isn't, so anonymous
        //     visitors don't spam a "no token" warning on every request.
        const requestPath = (config.url || '').split('?')[0];
        const publicExactEndpoints = new Set([
          '/auth/register',
          '/auth/login',
          '/auth/google',
          '/auth/apple',
          '/auth/forgot-password',
          '/auth/reset-password',
          '/oauth/config',
          '/health',
          '/camps'
        ]);
        const publicPrefixEndpoints = [
          '/invites/validate/',
          '/categories',
          '/perks',
          '/cities'
        ];
        const optionalAuthPrefixEndpoints = [
          '/camps/public/'
        ];

        // Check endpoint classification
        const isStrictlyPublicEndpoint =
          publicExactEndpoints.has(requestPath) ||
          publicPrefixEndpoints.some((prefix) => requestPath.startsWith(prefix));
        const isOptionalAuthEndpoint =
          optionalAuthPrefixEndpoints.some((prefix) => requestPath.startsWith(prefix));

        // Strictly-public endpoints: never attach a token (avoids stale-token login loops).
        // Optional-auth endpoints: attach if we have one, stay quiet if we don't.
        // Everything else: attach if we have one, warn if we don't (the endpoint likely requires it).
        if (isStrictlyPublicEndpoint) {
          // Ensure public endpoints never carry stale Authorization headers.
          if (config.headers?.Authorization) {
            delete config.headers.Authorization;
          }
          console.log('🔓 [API Interceptor] Public endpoint - no auth required:', config.url);
        } else if (isOptionalAuthEndpoint) {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            
            // Log token info for debugging (only first/last few chars for security)
            console.log('🔑 [API Interceptor] Token present:', 
              `${token.substring(0, 10)}...${token.substring(token.length - 10)}`
            );
            
            // Try to decode JWT to check expiration (without verifying signature)
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              const now = Math.floor(Date.now() / 1000);
              const expiresIn = payload.exp - now;
              
              if (expiresIn < 0) {
                console.warn('⚠️ [API Interceptor] Token appears to be expired!', {
                  expired: new Date(payload.exp * 1000).toISOString(),
                  now: new Date(now * 1000).toISOString()
                });
              } else if (expiresIn < 300) { // Less than 5 minutes
                console.warn('⚠️ [API Interceptor] Token expires soon:', expiresIn, 'seconds');
              } else {
                console.log('✅ [API Interceptor] Token valid for:', Math.floor(expiresIn / 60), 'minutes');
              }
            } catch (e) {
              console.error('❌ [API Interceptor] Could not decode token:', e);
            }
          } else {
            console.warn('⚠️ [API Interceptor] No token found - protected endpoint may fail');
          }
        }
        
        console.log('🔄 [API Interceptor] Request:', config.method?.toUpperCase(), config.url);
        if (config.data) {
          // Handle FormData specially (can't JSON.stringify it)
          if (config.data instanceof FormData) {
            console.log('🔄 [API Interceptor] Request Data: FormData with entries:');
            for (const [key, value] of config.data.entries()) {
              if (value instanceof File) {
                console.log(`  - ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
              } else {
                console.log(`  - ${key}:`, value);
              }
            }
          } else {
            console.log('🔄 [API Interceptor] Request Data:', JSON.stringify(sanitizeForLog(config.data), null, 2));
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ [API Interceptor] Response:', response.config.method?.toUpperCase(), response.config.url);
        console.log('✅ [API Interceptor] Response Data:', JSON.stringify(response.data, null, 2));
        return response;
      },
      (error) => {
        // Detailed error logging for all error types
        if (error.response) {
          // Server responded with error status
          console.error('❌ [API Interceptor] Server Error:', {
            status: error.response.status,
            data: error.response.data,
            url: error.config?.url,
            method: error.config?.method
          });
        } else if (error.request) {
          // Request made but no response (timeout, network failure, CORS)
          console.error('❌ [API Interceptor] Network/Timeout Error:', {
            code: error.code,
            message: error.message,
            url: error.config?.url,
            method: error.config?.method,
            timeout: error.config?.timeout
          });
        } else {
          // Something else happened
          console.error('❌ [API Interceptor] Request Setup Error:', {
            message: error.message,
            url: error.config?.url
          });
        }
        
        // Handle auth errors specifically
        if (error.response?.status === 401 || error.response?.status === 403) {
          const errorMessage = error.response?.data?.message || '';
          console.error('❌ [API Interceptor] Auth error:', errorMessage);
          
          // Only redirect to login if it's specifically a token issue
          // Don't redirect for other 403 errors (like permission denied)
          if (errorMessage.includes('Invalid or expired token') || 
              errorMessage.includes('Access token required') ||
              errorMessage === 'Invalid token') {
            console.warn('⚠️ [API Interceptor] Token invalid, clearing and redirecting to login');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            
            // Don't redirect if already on login page
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
          } else {
            // For other auth errors, let the component handle them
            console.log('ℹ️ [API Interceptor] Auth error will be handled by component:', errorMessage);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<{ token: string; user: User; isFirstLogin?: boolean }> {
    console.log('🔐 [DEBUG] API Service - Login attempt:', { email, baseURL: this.api.defaults.baseURL });
    try {
      const response: AxiosResponse<{ token: string; user: User; isFirstLogin?: boolean }> = await this.api.post('/auth/login', {
        email,
        password,
      });
      console.log('✅ [DEBUG] API Service - Login successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [DEBUG] API Service - Login failed:', error);
      console.error('❌ [DEBUG] Error details:', error.response?.data);
      throw error;
    }
  }

  async register(userData: RegisterData): Promise<{ token: string; user: User; isNewAccount?: boolean }> {
    console.log('🔐 [DEBUG] API Service - Registration attempt:', { email: userData.email, baseURL: this.api.defaults.baseURL });
    try {
      // Registration can take longer due to password hashing, DB writes, and email queuing
      // Use a 30-second timeout for registration
      const response: AxiosResponse<{ token: string; user: User; isNewAccount?: boolean }> = await this.api.post('/auth/register', userData, {
        timeout: 30000 // 30 seconds for registration
      });
      console.log('✅ [DEBUG] API Service - Registration successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [DEBUG] API Service - Registration failed:', error);
      console.error('❌ [DEBUG] Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      throw error;
    }
  }

  async getCurrentUser(): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.get('/auth/me');
    return response.data;
  }

  async refreshToken(): Promise<{ token: string }> {
    const response: AxiosResponse<{ token: string }> = await this.api.post('/auth/refresh');
    return response.data;
  }

  // User endpoints
  async getUserProfile(): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.get('/users/profile');
    return response.data;
  }

  async updateUserProfile(userData: Partial<User>): Promise<{ user: User }> {
    console.log('🔄 [API] updateUserProfile called with:', userData);
    console.log('🔄 [API] playaName in request:', userData.playaName);
    const response: AxiosResponse<{ user: User }> = await this.api.put('/users/profile', userData);
    console.log('✅ [API] updateUserProfile response:', response.data);
    console.log('✅ [API] playaName in response:', response.data.user?.playaName);
    return response.data;
  }


  async updatePreferences(preferences: any): Promise<{ preferences: any }> {
    const response: AxiosResponse<{ preferences: any }> = await this.api.put('/users/preferences', preferences);
    return response.data;
  }

  async searchUsers(params: {
    q?: string;
    location?: string;
    skills?: string;
    experience?: string;
  }): Promise<{ users: User[] }> {
    const response: AxiosResponse<{ users: User[] }> = await this.api.get('/users/search', { params });
    return response.data;
  }

  async searchCities(query: string): Promise<{ cities: StructuredLocation[] }> {
    const response: AxiosResponse<{ cities: StructuredLocation[] }> = await this.api.get('/cities', {
      params: { query }
    });
    return response.data;
  }

  // Camp endpoints
  async getCamps(params?: {
    page?: number;
    limit?: number;
    search?: string;
    location?: string;
    theme?: string;
    size?: string;
    recruiting?: boolean;
  }): Promise<PaginatedResponse<Camp>> {
    const response: AxiosResponse<PaginatedResponse<Camp>> = await this.api.get('/camps', { params });
    return response.data;
  }

  async getCamp(id: string): Promise<{ camp: Camp }> {
    const response: AxiosResponse<{ camp: Camp }> = await this.api.get(`/camps/${id}`);
    return response.data;
  }

  async createCamp(campData: Partial<Camp>): Promise<{ camp: Camp }> {
    const response: AxiosResponse<{ camp: Camp }> = await this.api.post('/camps', campData);
    return response.data;
  }

  async getMyCamp(): Promise<Camp> {
    const response: AxiosResponse<Camp> = await this.api.get('/camps/my-camp');
    return response.data;
  }


  async deleteCamp(id: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/camps/${id}`);
    return response.data;
  }

  async getCampMembers(campId: string): Promise<{ members: Member[] }> {
    const response: AxiosResponse<{ members: Member[] }> = await this.api.get(`/role-management/camp/${campId}/members`);
    return response.data;
  }

  async getCampSignupForm(campId: string): Promise<{ campName: string; signupForm: any }> {
    const response: AxiosResponse<{ campName: string; signupForm: any }> = await this.api.get(`/camps/${campId}/signup-form`);
    return response.data;
  }

  async updateCampSignupForm(campId: string, signupForm: any): Promise<{ signupForm: any }> {
    const response: AxiosResponse<{ signupForm: any }> = await this.api.put(`/camps/${campId}/signup-form`, signupForm);
    return response.data;
  }

  // Member endpoints
  async applyToCamp(campId: string, applicationData: Record<string, any>): Promise<{ member: Member }> {
    const response: AxiosResponse<{ member: Member }> = await this.api.post('/members/apply', {
      campId,
      applicationData,
    });
    return response.data;
  }

  async getMyApplications(): Promise<{ applications: Member[] }> {
    const response: AxiosResponse<{ applications: Member[] }> = await this.api.get('/members/my-applications');
    return response.data;
  }

  async approveMember(memberId: string, status: 'active' | 'rejected', reviewNotes?: string): Promise<{ member: Member }> {
    const response: AxiosResponse<{ member: Member }> = await this.api.put(`/members/${memberId}/approve`, {
      status,
      reviewNotes,
    });
    return response.data;
  }

  async changeMemberRole(memberId: string, newRole: 'member' | 'project-lead' | 'camp-lead', reason?: string): Promise<{ member: Member }> {
    const response: AxiosResponse<{ member: Member }> = await this.api.put(`/members/${memberId}/role`, {
      newRole,
      reason,
    });
    return response.data;
  }

  async updateMemberStatus(memberId: string, status: 'active' | 'inactive' | 'suspended', reason?: string): Promise<{ member: Member }> {
    const response: AxiosResponse<{ member: Member }> = await this.api.put(`/members/${memberId}/status`, {
      status,
      reason,
    });
    return response.data;
  }

  async addMemberContribution(memberId: string, contribution: any): Promise<{ contribution: any }> {
    const response: AxiosResponse<{ contribution: any }> = await this.api.post(`/members/${memberId}/contributions`, contribution);
    return response.data;
  }

  async getMember(memberId: string): Promise<{ member: Member }> {
    const response: AxiosResponse<{ member: Member }> = await this.api.get(`/members/${memberId}`);
    return response.data;
  }

  async importMembersCsv(params: {
    file: File;
    campId?: string;
    confirm?: boolean;
    mapping?: Record<string, string>;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('file', params.file);
    if (params.campId) formData.append('campId', params.campId);
    if (params.confirm) formData.append('confirm', 'true');
    if (params.mapping) formData.append('mapping', JSON.stringify(params.mapping));
    const response = await this.api.post('/members/import-csv', formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  }

  // Upload endpoints
  async uploadProfilePhoto(file: File): Promise<{ photoUrl: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response: AxiosResponse<{ photoUrl: string }> = await this.api.post('/upload/profile-photo', formData, {
      // Don't set Content-Type manually - let Axios set it with the boundary parameter
      transformRequest: [(data) => data], // Don't transform FormData
    });
    return response.data;
  }

  async uploadCampPhotos(files: File[]): Promise<{ photos: string[] }> {
    const formData = new FormData();
    files.forEach(file => formData.append('photos', file));
    const response: AxiosResponse<{ photos: string[] }> = await this.api.post('/upload/camp-photos', formData, {
      // Don't set Content-Type manually - let Axios set it with the boundary parameter
      transformRequest: [(data) => data], // Don't transform FormData
    });
    return response.data;
  }

  async uploadCampPhoto(campId: string, file: File, caption?: string, isPrimary?: boolean): Promise<{ photo: any }> {
    const formData = new FormData();
    formData.append('photo', file);
    if (caption) formData.append('caption', caption);
    if (isPrimary) formData.append('isPrimary', isPrimary.toString());
    
    const response: AxiosResponse<{ photo: any }> = await this.api.post(`/upload/camp-photo/${campId}`, formData, {
      // Don't set Content-Type manually - let Axios set it with the boundary parameter
      transformRequest: [(data) => data], // Don't transform FormData
    });
    return response.data;
  }

  async deleteCampPhoto(campId: string): Promise<{ message: string; remainingPhotos: number }> {
    const response: AxiosResponse<{ message: string; remainingPhotos: number }> = await this.api.delete(`/upload/camp-photo/${campId}`);
    return response.data;
  }

  async deletePhoto(publicId: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/upload/photo/${publicId}`);
    return response.data;
  }

  // Admin endpoints
  async getAdminDashboard(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/admin/dashboard');
    return response.data;
  }

  async getAdminUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    accountType?: string;
    status?: string;
  }): Promise<PaginatedResponse<User>> {
    const response: AxiosResponse<PaginatedResponse<User>> = await this.api.get('/admin/users', { params });
    return response.data;
  }

  async getAdminCamps(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    recruiting?: boolean;
  }): Promise<PaginatedResponse<Camp>> {
    const response: AxiosResponse<PaginatedResponse<Camp>> = await this.api.get('/admin/camps', { params });
    return response.data;
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.put(`/admin/users/${userId}/status`, { isActive });
    return response.data;
  }

  async updateCampStatus(campId: string, status: string): Promise<{ camp: Camp }> {
    const response: AxiosResponse<{ camp: Camp }> = await this.api.put(`/admin/camps/${campId}/status`, { status });
    return response.data;
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.put(`/admin/users/${userId}`, userData);
    return response.data;
  }

  async updateCamp(campId: string, campData: Partial<Camp>): Promise<{ camp: Camp }> {
    const response: AxiosResponse<{ camp: Camp }> = await this.api.put(`/admin/camps/${campId}`, campData);
    return response.data;
  }

  async uploadAdminCampPhoto(campId: string, formData: FormData): Promise<{ photo: string; camp: Camp }> {
    const response: AxiosResponse<{ photo: string; camp: Camp }> = await this.api.post(`/admin/camps/${campId}/upload-photo`, formData, {
      // Don't set Content-Type manually - let Axios set it with the boundary parameter
      transformRequest: [(data) => data], // Don't transform FormData
    });
    return response.data;
  }

  async createAdmin(userId: string, role: string, permissions?: any): Promise<{ admin: Admin }> {
    const response: AxiosResponse<{ admin: Admin }> = await this.api.post('/admin/admins', {
      userId,
      role,
      permissions,
    });
    return response.data;
  }

  async getAdmins(): Promise<{ admins: Admin[] }> {
    const response: AxiosResponse<{ admins: Admin[] }> = await this.api.get('/admin/admins');
    return response.data;
  }

  async removeAdmin(adminId: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/admin/admins/${adminId}`);
    return response.data;
  }

  async getEmailTemplates(): Promise<{ templates: Array<{
    _id: string;
    key: string;
    name: string;
    description: string;
    subject: string;
    htmlContent: string;
    textContent: string;
    variables: string[];
    isActive: boolean;
  }> }> {
    const response = await this.api.get('/admin/email-templates');
    return response.data;
  }

  async updateEmailTemplate(
    key: string,
    payload: {
      subject: string;
      htmlContent: string;
      textContent: string;
      variables?: string[];
      isActive?: boolean;
    }
  ): Promise<{ template: any }> {
    const response = await this.api.put(`/admin/email-templates/${key}`, payload);
    return response.data;
  }

  async previewEmailTemplate(key: string, data: Record<string, string>): Promise<{
    subject: string;
    htmlContent: string;
    textContent: string;
  }> {
    const response = await this.api.post(`/admin/email-templates/${key}/preview`, { data });
    return response.data;
  }

  // Role Management endpoints
  async getRoleHierarchy(): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.get('/role-management/roles/hierarchy');
    return response.data;
  }


  async getRoleChangeRequests(campId: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.get(`/role-management/camp/${campId}/role-requests`);
    return response.data;
  }

  async updateMemberRole(memberId: string, roleData: { newRole: string; reason: string; campId: string }): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.put(`/role-management/members/${memberId}/role`, roleData);
    return response.data;
  }

  async requestRoleChange(memberId: string, requestData: { requestedRole: string; reason: string }): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post(`/role-management/members/${memberId}/request-role-change`, requestData);
    return response.data;
  }

  // Generic HTTP methods for direct API calls
  // Change email
  async changeEmail(newEmail: string): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.put('/users/change-email', {
      newEmail
    });
    return response.data;
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put('/users/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }


  // Call Slot methods
  async getCallSlots(campId: string): Promise<CallSlot[]> {
    const response: AxiosResponse<CallSlot[]> = await this.api.get(`/call-slots/camp/${campId}`);
    return response.data;
  }

  async createCallSlot(callSlotData: any): Promise<CallSlot> {
    const response: AxiosResponse<CallSlot> = await this.api.post('/call-slots', callSlotData);
    return response.data;
  }

  async updateCallSlot(id: string, updates: any): Promise<CallSlot> {
    const response: AxiosResponse<CallSlot> = await this.api.put(`/call-slots/${id}`, updates);
    return response.data;
  }

  async deleteCallSlot(id: string): Promise<void> {
    await this.api.delete(`/call-slots/${id}`);
  }

  async getAvailableCallSlots(campId: string): Promise<CallSlot[]> {
    const response: AxiosResponse<CallSlot[]> = await this.api.get(`/call-slots/available/${campId}`);
    return response.data;
  }

  // Task methods
  async getTasks(campId: string): Promise<Task[]> {
    const response: AxiosResponse<Task[]> = await this.api.get(`/tasks/camp/${campId}`);
    return response.data;
  }

  async getTaskByCode(taskIdCode: string): Promise<Task> {
    const response: AxiosResponse<Task> = await this.api.get(`/tasks/code/${taskIdCode}`);
    return response.data;
  }

  async getTaskById(taskId: string): Promise<Task> {
    const response: AxiosResponse<Task> = await this.api.get(`/tasks/${taskId}`);
    return response.data;
  }

  async getAssignedTasks(userId: string): Promise<Task[]> {
    const response: AxiosResponse<Task[]> = await this.api.get(`/tasks/assigned/${userId}`);
    return response.data;
  }

  async createTask(taskData: any): Promise<Task> {
    const response: AxiosResponse<Task> = await this.api.post('/tasks', taskData);
    return response.data;
  }

  async updateTask(id: string, updates: any): Promise<Task> {
    const response: AxiosResponse<Task> = await this.api.put(`/tasks/${id}`, updates);
    return response.data;
  }

  async deleteTask(id: string): Promise<void> {
    await this.api.delete(`/tasks/${id}`);
  }

  async assignTask(id: string, assignedTo: string[]): Promise<Task> {
    const response: AxiosResponse<Task> = await this.api.post(`/tasks/${id}/assign`, { assignedTo });
    return response.data;
  }

  async postTaskComment(taskId: string, text: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/tasks/${taskId}/comments`, { text });
    return response.data;
  }

  // Invite methods
  async getInviteTemplates(campId: string): Promise<{inviteTemplateEmail: string, inviteTemplateSMS: string}> {
    const response = await this.api.get(`/camps/${campId}/invites/template`);
    return response.data;
  }

  async updateInviteTemplates(campId: string, templates: {inviteTemplateEmail: string, inviteTemplateSMS: string}): Promise<any> {
    const response = await this.api.put(`/camps/${campId}/invites/template`, templates);
    return response.data;
  }

  async sendInvites(inviteData: {recipients: string[], method: 'email' | 'sms', campId: string}): Promise<any> {
    const response = await this.api.post('/invites', inviteData);
    return response.data;
  }

  async getCampInvites(campId: string, status?: string): Promise<any> {
    const url = status ? `/camps/${campId}/invites?status=${status}` : `/camps/${campId}/invites`;
    const response = await this.api.get(url);
    return response.data;
  }

  // Notifications
  async getNotifications(params?: { page?: number; limit?: number; unreadOnly?: boolean }): Promise<NotificationListResponse> {
    const response: AxiosResponse<NotificationListResponse> = await this.api.get('/notifications', { params });
    return response.data;
  }

  async markNotificationRead(notificationId: string): Promise<{ notification: any }> {
    const response: AxiosResponse<{ notification: any }> = await this.api.put(`/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsRead(): Promise<{ message: string; modifiedCount: number }> {
    const response: AxiosResponse<{ message: string; modifiedCount: number }> = await this.api.put('/notifications/read-all');
    return response.data;
  }

  // Shift/member experience
  async getMyShifts(): Promise<MyShiftsResponse> {
    const response: AxiosResponse<MyShiftsResponse> = await this.api.get('/shifts/my-shifts');
    return response.data;
  }

  async signUpForShift(shiftId: string): Promise<any> {
    const response = await this.api.post(`/shifts/shifts/${shiftId}/signup`);
    return response.data;
  }

  async cancelShiftSignup(shiftId: string): Promise<any> {
    const response = await this.api.delete(`/shifts/shifts/${shiftId}/signup`);
    return response.data;
  }

  async setEventShiftDropsLocked(eventId: string, shiftDropsLocked: boolean): Promise<{ event: any }> {
    const response = await this.api.patch(`/shifts/events/${eventId}/shift-drop-lock`, { shiftDropsLocked });
    return response.data;
  }

  async getShiftAssignees(shiftId: string): Promise<{
    shiftId: string;
    isDirectAssignmentLocked: boolean;
    confirmedUsers: Array<{ userId: string; firstName: string; lastName: string; email: string; playaName?: string; isLead?: boolean; isActiveRosterMember?: boolean; signupType: 'DIRECT_ASSIGNMENT' | 'SELF_SIGNUP' }>;
    assignedUsers: Array<{ userId: string; firstName: string; lastName: string; email: string; playaName?: string; isLead?: boolean; isActiveRosterMember?: boolean }>;
    unassignedUsers: Array<{ userId: string; firstName: string; lastName: string; email: string; playaName?: string; isLead?: boolean }>;
  }> {
    const response = await this.api.get(`/shifts/shifts/${shiftId}/assignees`);
    return response.data;
  }

  async addShiftAssignees(shiftId: string, userIds: string[]): Promise<{ addedCount: number; addedUserIds: string[] }> {
    const response = await this.api.post(`/shifts/shifts/${shiftId}/assignees/add`, { userIds });
    return response.data;
  }

  async removeShiftAssignee(shiftId: string, userId: string): Promise<{
    removedUserId: string;
    isDirectAssignmentLocked: boolean;
  }> {
    const response = await this.api.delete(`/shifts/shifts/${shiftId}/assignees/${userId}`);
    return response.data;
  }

  async dropMemberFromShift(shiftId: string, userId: string): Promise<{
    removedUserId: string;
    wasDirectAssignment: boolean;
    assignmentMode: 'ALL_ROSTER' | 'LEADS_ONLY' | 'SELECTED_USERS';
  }> {
    const response = await this.api.delete(`/shifts/shifts/${shiftId}/signups/${userId}`);
    return response.data;
  }

  async inviteEntireRosterToAllShifts(
    campId?: string,
    options?: {
      previewOnly?: boolean;
      skipRecentDays?: number;
      scheduleAt?: string;
      memberIds?: string[];
      onlyWithoutShifts?: boolean;
    }
  ): Promise<{ message: string; invitedCount: number; availableShiftCount: number; recipientPreview?: { existingUsers: number; rosterOnly: number; total: number } }> {
    const url = campId ? `/shifts/events/invite-entire-roster?campId=${campId}` : '/shifts/events/invite-entire-roster';
    const response: AxiosResponse<{ message: string; invitedCount: number; availableShiftCount: number; recipientPreview?: { existingUsers: number; rosterOnly: number; total: number } }> = await this.api.post(url, options || {});
    return response.data;
  }

  // Surveys
  async getCampSurveys(campId: string): Promise<{ surveys: Survey[] }> {
    const response: AxiosResponse<{ surveys: Survey[] }> = await this.api.get(`/surveys/camp/${campId}`);
    return response.data;
  }

  async getSurveyDetails(surveyId: string): Promise<{
    survey: Survey;
    questions: SurveyQuestion[];
    viewer: any;
    completionStats?: any;
  }> {
    const response = await this.api.get(`/surveys/${surveyId}`);
    return response.data;
  }

  async createSurveyDraft(payload: {
    campId: string;
    title: string;
    description?: string;
    questions?: SurveyQuestion[];
  }): Promise<{ survey: Survey; questions: SurveyQuestion[] }> {
    const response = await this.api.post('/surveys', payload);
    return response.data;
  }

  async updateSurveyDraft(
    surveyId: string,
    payload: { title?: string; description?: string; questions?: SurveyQuestion[] }
  ): Promise<{ survey: Survey; questions: SurveyQuestion[] }> {
    const response = await this.api.put(`/surveys/${surveyId}`, payload);
    return response.data;
  }

  async sendSurvey(
    surveyId: string,
    payload?: {
      assignmentMode?: 'ALL_ROSTER' | 'LEADS_ONLY' | 'SELECTED_USERS';
      selectedUserIds?: string[];
      manualAddIds?: string[];
      manualRemoveIds?: string[];
    }
  ): Promise<{
    message: string;
    surveyId: string;
    assignedCount: number;
    skippedExistingCount?: number;
    totalAssignedCount?: number;
    assignmentMode: string;
  }> {
    const response = await this.api.post(`/surveys/${surveyId}/send`, payload || {});
    return response.data;
  }

  async closeSurvey(surveyId: string): Promise<{ message: string; survey: Survey }> {
    const response = await this.api.post(`/surveys/${surveyId}/close`);
    return response.data;
  }

  async deleteSurvey(surveyId: string): Promise<{ message: string; surveyId: string; deleted?: Record<string, number> }> {
    const response = await this.api.delete(`/surveys/${surveyId}`);
    return response.data;
  }

  async importSurveyFromPublicForm(payload: {
    campId: string;
    url: string;
    saveDraft?: boolean;
  }): Promise<{
    message: string;
    survey?: Survey & { questions?: SurveyQuestion[]; importWarnings?: string[] };
    suggestion?: any;
    canContinueManually?: boolean;
  }> {
    const response = await this.api.post('/surveys/import-suggestion', payload);
    return response.data;
  }

  async getMyPendingSurveys(): Promise<{
    pendingSurveys: PendingSurveyItem[];
    completedSurveys: PendingSurveyItem[];
  }> {
    const response = await this.api.get('/surveys/my-pending');
    return response.data;
  }

  async getSurveyEligibleMembers(
    surveyId: string,
    query?: string
  ): Promise<{
    submitterMemberId: string;
    submitterMember?: { memberId: string; name: string; email?: string; alreadyCovered: boolean; eligible: boolean; coveredByResponseId?: string | null };
    eligibleMembers: Array<{ memberId: string; name: string; email?: string; alreadyCovered: boolean; eligible: boolean; coveredByResponseId?: string | null }>;
  }> {
    const response = await this.api.get(`/surveys/${surveyId}/eligible-members`, {
      params: query ? { q: query } : undefined
    });
    return response.data;
  }

  async submitSurveyResponse(
    surveyId: string,
    payload: { coveredMemberIds: string[]; answers: Array<{ questionId: string; blockType: string; value: any; valueType?: string }> }
  ): Promise<{
    message: string;
    responseId: string;
    coveredMemberIds: string[];
    emailReceipt?: {
      submitterEmail?: string;
      delegatedMemberNames?: string[];
    };
  }> {
    const response = await this.api.post(`/surveys/${surveyId}/responses`, payload);
    return response.data;
  }

  async editSurveyResponse(
    surveyId: string,
    responseId: string,
    payload: { answers?: Array<{ questionId: string; blockType: string; value: any; valueType?: string }>; coveredMemberIds?: string[]; eapSent?: boolean; editReason?: string }
  ): Promise<{ message: string; response: any }> {
    const response = await this.api.put(`/surveys/${surveyId}/responses/${responseId}`, payload);
    return response.data;
  }

  async deleteSurveyResponse(
    surveyId: string,
    responseId: string
  ): Promise<{ message: string; responseId: string; resetMemberIds: string[]; deleted?: Record<string, number> }> {
    const response = await this.api.delete(`/surveys/${surveyId}/responses/${responseId}`);
    return response.data;
  }

  async getSurveyResponses(surveyId: string): Promise<{ responses: Array<any> }> {
    const response = await this.api.get(`/surveys/${surveyId}/responses`);
    return response.data;
  }

  async getSurveyRosterGroups(campId: string, surveyId?: string): Promise<{
    surveyId: string | null;
    surveyTitle?: string;
    groupsByPrimary: Record<string, { responseId: string; surveyId: string; extraCount: number; otherMemberIds: string[]; otherNames: string[] }>;
    memberToPrimary: Record<string, string>;
  }> {
    const response = await this.api.get(`/surveys/camp/${campId}/roster-groups`, {
      params: surveyId ? { surveyId } : undefined
    });
    return response.data;
  }

  // Generic HTTP methods
  async get(url: string, config?: any): Promise<any> {
    const response = await this.api.get(url, config);
    return response.data;
  }

  async post(url: string, data?: any, config?: any): Promise<any> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put(url: string, data?: any, config?: any): Promise<any> {
    console.log('🔄 [API] PUT request to:', url, 'with data:', data);
    const response = await this.api.put(url, data, config);
    console.log('✅ [API] PUT response:', response.data);
    return response.data;
  }

  async patch(url: string, data?: any, config?: any): Promise<any> {
    const response = await this.api.patch(url, data, config);
    return response.data;
  }

  async delete(url: string, config?: any): Promise<any> {
    console.log(`🗑️ [API] DELETE request to: ${url}`);
    try {
      const response = await this.api.delete(url, config);
      console.log(`✅ [API] DELETE ${url} success:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ [API] DELETE ${url} failed:`, error);
      throw error;
    }
  }

  // Camp Lead role management methods
  async grantCampLeadRole(
    memberId: string,
    options: LeadRoleGrantOptions = {}
  ): Promise<CampLeadRoleResponse> {
    const response: AxiosResponse<CampLeadRoleResponse> = await this.api.post(
      `/rosters/member/${memberId}/grant-camp-lead`,
      options.replaceExistingRole ? { replaceExistingRole: true } : undefined
    );
    return response.data;
  }

  async revokeCampLeadRole(memberId: string): Promise<{ message: string; memberId: string; memberName: string; isCampLead: boolean }> {
    const response: AxiosResponse<{ message: string; memberId: string; memberName: string; isCampLead: boolean }> = await this.api.post(`/rosters/member/${memberId}/revoke-camp-lead`);
    return response.data;
  }

  async grantEventsLeadRole(
    memberId: string,
    campId?: string,
    options: LeadRoleGrantOptions = {}
  ): Promise<EventsLeadRoleResponse> {
    const payload = {
      ...(campId ? { campId } : {}),
      ...(options.replaceExistingRole ? { replaceExistingRole: true } : {})
    };
    const response: AxiosResponse<EventsLeadRoleResponse> = await this.api.post(
      `/rosters/member/${memberId}/grant-events-lead`,
      Object.keys(payload).length > 0 ? payload : undefined
    );
    return response.data;
  }

  async revokeEventsLeadRole(memberId: string, campId?: string): Promise<{ message: string; memberId: string; memberName: string; isEventsLead: boolean }> {
    const response: AxiosResponse<{ message: string; memberId: string; memberName: string; isEventsLead: boolean }> = await this.api.post(
      `/rosters/member/${memberId}/revoke-events-lead`,
      campId ? { campId } : undefined
    );
    return response.data;
  }

  async previewDuesEmail(
    rosterId: string,
    memberId: string,
    payload: { actionType?: 'instructions' | 'receipt'; targetStatus?: 'UNPAID' | 'INSTRUCTED' | 'PAID'; subject?: string; body?: string }
  ): Promise<any> {
    const response = await this.api.post(`/rosters/${rosterId}/members/${memberId}/dues/preview`, payload);
    return response.data;
  }

  async sendDuesEmail(
    rosterId: string,
    memberId: string,
    payload: { actionType: 'instructions' | 'receipt'; subject?: string; body?: string; saveAsCampDefault?: boolean }
  ): Promise<any> {
    const response = await this.api.post(`/rosters/${rosterId}/members/${memberId}/dues/send-email`, payload);
    return response.data;
  }

  async updateMemberDuesStatus(
    rosterId: string,
    memberId: string,
    payload: { duesStatus: 'UNPAID' | 'INSTRUCTED' | 'PAID'; emailPreview?: { subject: string; body: string }; saveAsCampDefault?: boolean }
  ): Promise<any> {
    const response = await this.api.put(`/rosters/${rosterId}/members/${memberId}/dues`, payload);
    return response.data;
  }

  async getDuesTemplates(rosterId: string): Promise<any> {
    const response = await this.api.get(`/rosters/${rosterId}/dues/templates`);
    return response.data;
  }

  async updateDuesTemplates(
    rosterId: string,
    payload: {
      instructions: { subject?: string; body?: string };
      receipt: { subject?: string; body?: string };
    }
  ): Promise<any> {
    const response = await this.api.put(`/rosters/${rosterId}/dues/templates`, payload);
    return response.data;
  }

  async previewMealPlanEmail(
    rosterId: string,
    memberId: string,
    payload: { actionType?: 'instructions' | 'receipt'; targetStatus?: 'UNPAID' | 'INSTRUCTED' | 'PAID'; subject?: string; body?: string }
  ): Promise<any> {
    const response = await this.api.post(`/rosters/${rosterId}/members/${memberId}/meal-plan/preview`, payload);
    return response.data;
  }

  async sendMealPlanEmail(
    rosterId: string,
    memberId: string,
    payload: { actionType: 'instructions' | 'receipt'; subject?: string; body?: string; saveAsCampDefault?: boolean }
  ): Promise<any> {
    const response = await this.api.post(`/rosters/${rosterId}/members/${memberId}/meal-plan/send-email`, payload);
    return response.data;
  }

  async updateMemberMealPlanStatus(
    rosterId: string,
    memberId: string,
    payload: { mealPlanStatus: 'UNPAID' | 'INSTRUCTED' | 'PAID' | 'OPTED_OUT'; emailPreview?: { subject: string; body: string }; saveAsCampDefault?: boolean }
  ): Promise<any> {
    const response = await this.api.put(`/rosters/${rosterId}/members/${memberId}/meal-plan`, payload);
    return response.data;
  }

  async getMealPlanTemplates(rosterId: string): Promise<any> {
    const response = await this.api.get(`/rosters/${rosterId}/meal-plan/templates`);
    return response.data;
  }

  async updateMealPlanTemplates(
    rosterId: string,
    payload: {
      instructions: { subject?: string; body?: string };
      receipt: { subject?: string; body?: string };
    }
  ): Promise<any> {
    const response = await this.api.put(`/rosters/${rosterId}/meal-plan/templates`, payload);
    return response.data;
  }

  async getCampDuesTemplates(campId: string): Promise<any> {
    const response = await this.api.get(`/camps/${campId}/dues/templates`);
    return response.data;
  }

  async updateCampDuesTemplates(
    campId: string,
    payload: {
      instructions: { subject?: string; body?: string };
      receipt: { subject?: string; body?: string };
    }
  ): Promise<any> {
    const response = await this.api.put(`/camps/${campId}/dues/templates`, payload);
    return response.data;
  }

  async getCampMealPlanTemplates(campId: string): Promise<any> {
    const response = await this.api.get(`/camps/${campId}/meal-plan/templates`);
    return response.data;
  }

  async updateCampMealPlanTemplates(
    campId: string,
    payload: {
      instructions: { subject?: string; body?: string };
      receipt: { subject?: string; body?: string };
    }
  ): Promise<any> {
    const response = await this.api.put(`/camps/${campId}/meal-plan/templates`, payload);
    return response.data;
  }

  async getRosterCustomFields(campId: string): Promise<{ customFields: Array<{ key: string; label: string; type: string; options?: string[] }> }> {
    const response = await this.api.get('/rosters/custom-fields', { params: { campId } });
    return response.data;
  }

  async updateRosterCustomFields(
    campId: string,
    customFields: Array<{ key: string; label: string; type: 'text' | 'number' | 'dropdown' | 'checkbox'; options?: string[] }>
  ): Promise<{ customFields: any[] }> {
    const response = await this.api.put('/rosters/custom-fields', { campId, customFields });
    return response.data;
  }

  async updateRosterMember(rosterId: string, memberId: string, payload: Record<string, any>): Promise<any> {
    const response = await this.api.put(`/rosters/${rosterId}/members/${memberId}`, payload);
    return response.data;
  }

  // ── Shifts-Only Roster reminders ──────────────────────────────────────────
  // Invite nudge for members who have not signed up yet. Server skips signed-up
  // members and respects SOR_ROSTER_REMINDERS_ENABLED plus a 24h cooldown.
  async remindRosterMember(
    rosterId: string,
    memberId: string
  ): Promise<{ message: string; reminderType: 'invite'; lastReminderAt: string }> {
    const response = await this.api.post(`/rosters/${rosterId}/members/${memberId}/remind`);
    return response.data;
  }

  async bulkRemindRosterMembers(
    rosterId: string,
    memberIds: string[]
  ): Promise<{
    message: string;
    summary: { sent: number; cooldown: number; skipped: number; failed: number; total: number };
    results: Array<{
      memberId: string;
      status: 'sent' | 'cooldown' | 'skipped' | 'failed';
      lastReminderAt?: string;
      nextAllowedAt?: string;
      reason?: string;
      reminderType?: 'invite';
    }>;
  }> {
    const response = await this.api.post(`/rosters/${rosterId}/members/bulk-remind`, { memberIds });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
